/**
 * ONE ICON SET, ONE STROKE WEIGHT, ONE SIZE SCALE.
 *
 * The geometry below is the design package's icons/ folder: 24 grid, no fill,
 * currentColor, 1.75 stroke, round caps and joins. Where the package draws a
 * thing, the package's path is what is drawn here, unchanged.
 *
 * Four are ours and stay ours, and the reason is the same in every case: the
 * package's version drops information this product needs on screen.
 *   Roofline  the package draws a bare gable. Ours carries the bulbs, which
 *             is the difference between a roof and a lit roof, and this is a
 *             product about lit roofs.
 *   Garland   same, bulbs on the swag.
 *   Cluster   the package has no cluster at all.
 *   Bow       the package has no bow, and it is the accessory people place
 *             first.
 * Everything else that had no package equivalent - the moon, the tape, the
 * play triangle, the minus - keeps its own drawing at the same weight.
 */

type P = { size?: number; className?: string }

const S = ({ size = 18, children }: { size?: number; children: React.ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
)

export const IconUndo = ({ size }: P) => (
  <S size={size}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h11a5 5 0 0 1 0 10h-3" />
  </S>
)

/*
 * The back chevron on the editor's Jobs control. Outline, 24 grid, 1.75
 * stroke, round caps, currentColor, the same as every other icon in the set.
 */
export const IconChevronLeft = ({ size }: P) => (
  <S size={size}>
    <path d="m15 6-6 6 6 6" />
  </S>
)

export const IconRedo = ({ size }: P) => (
  <S size={size}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H9a5 5 0 0 0 0 10h3" />
  </S>
)

export const IconSun = ({ size }: P) => (
  <S size={size}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </S>
)

export const IconMoon = ({ size }: P) => (
  <S size={size}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </S>
)

export const IconPresent = ({ size }: P) => (
  <S size={size}>
    <path d="M9 7.5v9l7.5-4.5L9 7.5Z" />
  </S>
)

export const IconExport = ({ size }: P) => (
  <S size={size}>
    <path d="M12 15V4" />
    <path d="m8 8 4-4 4 4" />
    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </S>
)

/** A tape measure, angled, with the tick marks a tape actually has. */
export const IconRuler = ({ size }: P) => (
  <S size={size}>
    <path d="M21.3 8.7 8.7 21.3a2 2 0 0 1-2.8 0l-3.2-3.2a2 2 0 0 1 0-2.8L15.3 2.7a2 2 0 0 1 2.8 0l3.2 3.2a2 2 0 0 1 0 2.8Z" />
    <path d="m7.5 10.5 2 2M10.5 7.5l2 2M13.5 4.5l2 2M4.5 13.5l2 2" />
  </S>
)

export const IconMore = ({ size }: P) => (
  <S size={size}>
    <circle cx="5" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="19" cy="12" r="1.5" fill="currentColor" />
  </S>
)

export const IconChevron = ({ size }: P) => (
  <S size={size}>
    <path d="m6 9 6 6 6-6" />
  </S>
)

export const IconSpark = ({ size }: P) => (
  <S size={size}>
    <path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9 12 3.5Z" />
    <path d="M18.5 3.2v3M20 4.7h-3" />
  </S>
)

/* ---- what you are lighting ---- */

/* OURS. The package draws a bare gable; the bulbs are the whole subject. */
export const IconRoofline = ({ size }: P) => (
  <S size={size}>
    <path d="M2 15 12 6l10 9" />
    <circle cx="5.2" cy="13.4" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="8.6" cy="10.4" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="12" cy="7.4" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="15.4" cy="10.4" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="18.8" cy="13.4" r="0.9" fill="currentColor" stroke="none" />
  </S>
)

export const IconTree = ({ size }: P) => (
  <S size={size}>
    <path d="M12 3 6 12h3l-4 6h14l-4-6h3L12 3Z" />
    <path d="M12 18v3" />
  </S>
)

export const IconBush = ({ size }: P) => (
  <S size={size}>
    <path d="M5 18a4 4 0 0 1 0-8 5 5 0 0 1 9-3 4 4 0 0 1 5 6 3 3 0 0 1-1 5H5Z" />
  </S>
)

/* OURS, same reason as Roofline: the package's garland has no bulbs on it. */
export const IconSwag = ({ size }: P) => (
  <S size={size}>
    <path d="M3 7c4.5 10 13.5 10 18 0" />
    <circle cx="7.4" cy="11.6" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="12" cy="13.4" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="16.6" cy="11.6" r="0.9" fill="currentColor" stroke="none" />
  </S>
)

export const IconColumn = ({ size }: P) => (
  <S size={size}>
    <path d="M6 4h12M6 20h12M9 4v16M15 4v16" />
  </S>
)

export const IconWreath = ({ size }: P) => (
  <S size={size}>
    <circle cx="12" cy="13" r="7" />
    <path d="M9 5.5 12 3l3 2.5" />
  </S>
)

/* ---- tools ---- */

export const IconSelect = ({ size }: P) => (
  <S size={size}>
    <path d="M5 3l14 8-6 2-3 6z" />
  </S>
)

export const IconMove = ({ size }: P) => (
  <S size={size}>
    <path d="M12 2v20M2 12h20M8 6l4-4 4 4M8 18l4 4 4-4M6 8l-4 4 4 4M18 8l4 4-4 4" />
  </S>
)

export const IconMeasure = ({ size }: P) => (
  <S size={size}>
    <rect x="2.5" y="8" width="19" height="8" rx="1.6" />
    <path d="M7 8v3M11 8v4M15 8v3M19 8v4" />
  </S>
)

export const IconFit = ({ size }: P) => (
  <S size={size}>
    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
  </S>
)

export const IconFullscreen = ({ size }: P) => (
  <S size={size}>
    <path d="M8 4H5.5A1.5 1.5 0 0 0 4 5.5V8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
  </S>
)

export const IconMinus = ({ size }: P) => (
  <S size={size}>
    <path d="M6 12h12" />
  </S>
)

export const IconPlus = ({ size }: P) => (
  <S size={size}>
    <path d="M5 12h14M12 5v14" />
  </S>
)

export const IconClose = ({ size }: P) => (
  <S size={size}>
    <path d="M18 6 6 18M6 6l12 12" />
  </S>
)

export const IconPhoto = ({ size }: P) => (
  <S size={size}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-5-5L5 21" />
  </S>
)

/** OURS. A folded strand hanging from a branch: two rows joined at the bottom.
    The package has no cluster. */
export function IconCluster({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 5h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <path
        d="M9.5 5v10a2.5 2.5 0 0 0 5 0V5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="9.5" cy="8.5" r="1.15" fill="currentColor" />
      <circle cx="14.5" cy="10.5" r="1.15" fill="currentColor" />
      <circle cx="9.5" cy="12.5" r="1.15" fill="currentColor" />
      <circle cx="14.5" cy="14.5" r="1.15" fill="currentColor" />
      <circle cx="12" cy="17.2" r="1.15" fill="currentColor" />
    </svg>
  )
}

/** OURS. A bow, because that is the one everybody places first, and the
    package has none. */
export const IconBow = ({ size }: P) => (
  <S size={size}>
    <circle cx="12" cy="11" r="2.1" />
    <path d="M9.9 10.2 5.4 6.6a1.6 1.6 0 0 0-2.6 1.5l1.2 5.6a1.6 1.6 0 0 0 2.6.9l3.4-2.6" />
    <path d="M14.1 10.2l4.5-3.6a1.6 1.6 0 0 1 2.6 1.5l-1.2 5.6a1.6 1.6 0 0 1-2.6.9l-3.4-2.6" />
    <path d="M11 12.9 9.4 20M13 12.9 14.6 20" />
  </S>
)

/* ------------------------------------------------------- the workspace --- */

/*
 * FOUR DESTINATIONS AND A DOOR BACK TO THE PHOTOGRAPH.
 *
 * Unboxed line icons at the same stroke weight as everything above, because
 * the nav sits beside the editor's own controls all day and a second icon
 * style is what makes a product look assembled rather than designed.
 */

export const IconJobs = ({ size }: P) => (
  <S size={size}>
    <path d="m3 11 9-8 9 8v9a2 2 0 0 1-2 2h-4v-6h-6v6H5a2 2 0 0 1-2-2Z" />
  </S>
)

export const IconPeople = ({ size }: P) => (
  <S size={size}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
  </S>
)

export const IconBusiness = ({ size }: P) => (
  <S size={size}>
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </S>
)

export const IconBilling = ({ size }: P) => (
  <S size={size}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </S>
)

/** The door back into the editor when no job is open yet. */
export const IconDesign = ({ size }: P) => (
  <S size={size}>
    <path d="M18.4 2.6a2.1 2.1 0 0 1 3 3L11 16l-4 1 1-4Z" />
    <path d="M7 17c-1.7 0-3 1.3-3 3 0 .5-.5 1-1 1 1.4 1.4 3.4 1.5 4.6.3.8-.8 1-1.9.4-2.9" />
  </S>
)

/* Row markers on a pipeline card: where it is, who to call, what is on it,
   and when it was last touched. One stroke weight with everything else. */
export const IconPin = ({ size }: P) => (
  <S size={size}>
    <path d="M12 22s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12Z" />
    <circle cx="12" cy="10" r="2.5" />
  </S>
)
export const IconPhone = ({ size }: P) => (
  <S size={size}>
    <path d="M6.5 3h3l1.5 4-2 1.4a12 12 0 0 0 5.6 5.6L16 12l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.2 2 2 0 0 1 6.5 3Z" />
  </S>
)
export const IconTag = ({ size }: P) => (
  <S size={size}>
    <path d="M3 12.5V4.5A1.5 1.5 0 0 1 4.5 3h8L21 11.5 12.5 20 3 12.5Z" />
    <circle cx="7.5" cy="7.5" r="1.2" />
  </S>
)
/* A priced document. Same stroke, same 24 box, same corner radius as the rest. */
export const IconDoc = ({ size }: P) => (
  <S size={size}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6M8 13h8M8 17h8" />
  </S>
)
/* Settings: how the thing is set up, not a cog with teeth nobody can see at 18px. */
export const IconTool = ({ size }: P) => (
  <S size={size}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
  </S>
)
/* Stock: a box on a shelf, at the same stroke as everything else. */
export const IconBox = ({ size }: P) => (
  <S size={size}>
    <path d="m21 8-9-5-9 5 9 5 9-5Z" />
    <path d="M3 8v8l9 5 9-5V8" />
    <path d="M12 13v8" />
  </S>
)
/* A phone bar that has run out of room. Three dots, nothing clever. */
export const IconMoreDots = ({ size }: P) => (
  <S size={size}>
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </S>
)
export const IconCalendar = ({ size }: P) => (
  <S size={size}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </S>
)
export const IconBoard = ({ size }: P) => (
  <S size={size}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </S>
)
export const IconRows = ({ size }: P) => (
  <S size={size}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </S>
)

/* Markers on a figure card: what the number is about. */
export const IconHouse = ({ size }: P) => (
  <S size={size}>
    <path d="m3 11 9-8 9 8v9a2 2 0 0 1-2 2h-4v-6h-6v6H5a2 2 0 0 1-2-2Z" />
  </S>
)
export const IconBell = ({ size }: P) => (
  <S size={size}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
  </S>
)
export const IconMoney = ({ size }: P) => (
  <S size={size}>
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </S>
)

/* Destructive controls take a red icon beside red text. See RULES.md. */
export const IconTrash = ({ size }: P) => (
  <S size={size}>
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
  </S>
)

/* Share design sends a picture to a homeowner, so the mark is a paper plane
   rather than an upload tray. Straight off the reference frames. */
export const IconSend = ({ size }: P) => (
  <S size={size}>
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </S>
)

/* The mark inside the black tile at the left of the editor's bar. */
export const IconSparkMark = ({ size }: P) => (
  <S size={size}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
  </S>
)

/* A row that opens something else. The reference frames put it on N runs on
   the photo and on Adjust, pointing the way you are going, not down. */
export const IconChevronRight = ({ size }: P) => (
  <S size={size}>
    <path d="m9 6 6 6-6 6" />
  </S>
)
